import type { Center } from "../../../../../../services/orgService";

// Department templates by center type
export const DepartmentsByType: Record<string, string[]> = {
    // Hospital departments (HOS)
    HOS: [
        "Administration",
        "BioMedical",
        "Café",
        "Case Management",
        "Dietary (Kitchen)",
        "Emergency Treatment Area",
        "EVS (Environmental Services)",
        "Facilities",
        "HIM (Health Information Management)",
        "Human Resources",
        "Infection Prevention",
        "ICU (Intensive Care Unit)",
        "Laboratory",
        "Materials Management",
        "Medical Facilities Holdings",
        "Operating Room",
        "PACU (Post Anesthesia Care Unit)",
        "PAT (Pre-Admit Testing)",
        "Patient Access/Registration",
        "Pharmacy",
        "Physical Therapy (inpatient)",
        "Physical Therapy Center Allen",
        "Physical Therapy Center Frisco",
        "Physical Therapy Center Plano",
        "Physical Therapy Center Richardson",
        "Pain Clinic",
        "Preoperative",
        "PSU (Post Surgical Unit)",
        "Quality",
        "Quality/Infection Prevention",
        "Radiology",
        "SPD (Sterile Processing Department)",
        "Lobby",
        "Other"
    ],
    
    // Surgery Center departments (ASC)
    ASC: [
        "EVS (Environmental Services)",
        "Laboratory",
        "Materials Management",
        "Operating Room",
        "PACU (Post Anesthesia Care Unit)",
        "Patient Access/Registration",
        "Pharmacy",
        "Pain Clinic",
        "Preoperative",
        "SPD (Sterile Processing Department)",
        "PAT (Pre-Admit Testing)",
        "Lobby",
        "Other"
    ],
    
    // Hospital Outpatient Department (HOPD)
    HOPD: [
        "EVS (Environmental Services)",
        "Laboratory",
        "Materials Management",
        "Operating Room",
        "PACU (Post Anesthesia Care Unit)",
        "Patient Access/Registration",
        "Pharmacy",
        "Pain Clinic",
        "Preoperative",
        "SPD (Sterile Processing Department)",
        "PAT (Pre-Admit Testing)",
        "Lobby",
        "Other"
    ],
    
    // Physical Therapy Clinic departments (PTC)
    PTC: [
        "Administration",
        "Café",
        "Case Management",
        "Dietary (Kitchen)",
        "Emergency Treatment Area",
        "EVS (Environmental Services)",
        "Facilities",
        "HIM (Health Information Management)",
        "Human Resources",
        "ICU (Intensive Care Unit)",
        "Laboratory",
        "Materials Management",
        "Operating Room",
        "PACU (Post Anesthesia Care Unit)",
        "PAT (Pre-Admit Testing)",
        "Patient Access/Registration",
        "Pharmacy",
        "Physical Therapy (inpatient)",
        "Physical Therapy Center Allen",
        "Physical Therapy Center Frisco",
        "Physical Therapy Center Plano",
        "Physical Therapy Center Richardson",
        "Pain Clinic",
        "Preoperative",
        "PSU (Post Surgical Unit)",
        "Quality/Infection Prevention",
        "Radiology",
        "SPD (Sterile Processing Department)",
        "Lobby",
        "Other"
    ],
    
    // Corporate/Unknown departments (UNK)
    UNK: [
        "Human Resources",
        "Clinical Operations",
        "EVS (Environmental Services)",
        "Facilities",
        "Compliance",
        "Accounting",
        "Finance",
        "Materials Management",
        "Information Technology",
        "Managed Care",
        "Revenue Cycle Management",
        "Payroll",
        "Legal Department",
        "Communications and Branding",
        "Business Development",
        "Lobby",
        "Other"
    ]
};

/**
 * Generate departments dynamically based on center configuration from API
 * @param centerCode - The center code to filter by (optional)
 * @param centerType - The center type from the Center configuration (e.g., 'HOS', 'ASC', 'PTC', 'UNK')
 * @returns Array of department objects with centerCode and department name
 */
export const getDepartmentsForCenter = (centerCode: string, centerType?: string): Array<{centerCode: string, department: string}> => {
    const departments: Array<{centerCode: string, department: string}> = [];
    
    // Use the centerType from Center configuration, fallback to empty array if not found
    const departmentList = centerType ? (DepartmentsByType[centerType] || []) : [];
    
    departmentList.forEach(department => {
        departments.push({ centerCode, department });
    });
    
    return departments;
};

/**
 * Generate departments for all centers based on their centerType from configuration
 * @param centers - Array of Center objects from the organization API
 * @returns Array of department objects with centerCode and department name
 */
export const generateDepartmentsFromCenters = (centers: Center[]): Array<{centerCode: string, department: string}> => {
    const departments: Array<{centerCode: string, department: string}> = [];
    
    centers.forEach(center => {
        const departmentList = DepartmentsByType[center.centerType] || [];
        departmentList.forEach(department => {
            departments.push({ centerCode: center.code, department });
        });
    });
    
    return departments;
};

// Backward compatibility: Export empty array by default
// Components should use useCenterQuery to get center data and call getDepartmentsForCenter
//export const Departments: Array<{centerCode: string, department: string}> = [];
